import { GlobalApplicationState } from 'globalApplicationState';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loading from 'modules/common/components/loading';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import { Button, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { CloseIcon } from 'tinacms';
import TopicCreationBody from './common/topicCreationBody';
import { editTopic, getTopicGroups, topicSlice } from 'modules/topics/reducer';
import { TopicDetails } from 'modules/topics/models';
import { ThunkDispatch } from '@reduxjs/toolkit';

export const TopicEdit: React.FunctionComponent<ComponentProps> = (props: ComponentProps) => {
    const [currentEdits, setCurrentEdits] = React.useState<TopicDetails | undefined>(props.topic);
    const [isEdited, setIsEdited] = React.useState<boolean>(false);
    const [isError, setIsError] = React.useState<boolean>(false);

    const {
        topicGroups,
        isUpdating: isSaving,
    } = useSelector((state: GlobalApplicationState) => state.topics);

    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

    React.useEffect(() => {
        if (props.topic) setCurrentEdits(props.topic);
    }, [props.topic]);

    const saveTopicCreationInput = (input?: TopicDetails, edited?: boolean) => {
        setCurrentEdits(input);
        setIsEdited(edited ?? true);
    };

    const handleManualClose = () => {
        saveTopicCreationInput(props.topic, false); // reset
        dispatch(topicSlice.actions.SET_ERR_MSG(""));
        props.onClose();
    };

    const handleEdit = async () => {
        if (isSaving || !currentEdits || !props.topic) return;
        const payload = {
            id: props.topic.id,
            input: currentEdits
        };

        const isSuccessful = await dispatch(editTopic(payload)).unwrap();
        if (isSuccessful) dispatch(getTopicGroups());
    };

    const getTopicGroupName = (): string => {
        const index = topicGroups.findIndex(g => g.id === props.topic?.groupId);
        return topicGroups[index]?.name;
    };

    const isNotFilled = (): boolean =>
        currentEdits?.groupId?.trim().length === 0 ||
        currentEdits?.name.trim().length === 0;

    const getDialogHeader = (): JSX.Element =>
        <DialogTitle className="audience-dialog-title">
            Edit Topic
            <IconButton onClick={handleManualClose} size="medium">
                <CloseIcon />
            </IconButton>
        </DialogTitle>;

    const getDialogFooter = (): JSX.Element =>
        <DialogActions>
            <Button variant="text" onClick={handleManualClose}>Cancel</Button>
            <Button
                variant="contained"
                color="primary"
                disabled={isError || isNotFilled() || !isEdited}
                onClick={handleEdit}
            >
                Save
            </Button>
        </DialogActions>;

    return (
        <Dialog
            open={props.show}
            maxWidth={false}
            scroll="paper"
            onClose={handleManualClose}
            classes={{ paper: "audience-dialog" }}
        >
            {getDialogHeader()}
            <DialogContent className={"dialog-content"}>

                {!props.topic || !currentEdits ? <Loading /> :
                    <TopicCreationBody
                        topicGroups={topicGroups}
                        input={currentEdits}
                        setInput={saveTopicCreationInput}
                        setIsError={setIsError}
                        defaultGroup={
                            { label: getTopicGroupName(), id: props.topic?.groupId || "" }
                        }
                    />
                }
            </DialogContent>
            {getDialogFooter()}
        </Dialog>
    );
};

interface ComponentProps {
    topic?: TopicDetails,
    show: boolean,
    onClose: () => void,
}

export default TopicEdit;